import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';
import { getVersionedKey } from '@/utils/versionUtils';

/**
 * usePersistentState is a hook that allows you to store state in localStorage.
 * @param {string} key - The key to store the state in.
 * @param {T | (() => T)} defaultValue - The default value to store in localStorage.
 * @returns {T} - The state.
 * @returns {Dispatch<SetStateAction<T>>} - The setState function.
 */
const usePersistentState = <T>(
  key: string,
  defaultValue: T | (() => T)
): [T, Dispatch<SetStateAction<T>>] => {
  const versionedKey = getVersionedKey(key);

  const [state, setState] = useState<T>(() => {
    // If we are in test mode, return the default value instead of the stored value
    if (process.env.NODE_ENV === 'test') {
      const value = typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue;
      return value ?? ([] as unknown as T);
    }

    try {
      const storedValue = localStorage.getItem(versionedKey);
      return storedValue ? JSON.parse(storedValue) : defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key ${versionedKey}:`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    try {
      localStorage.setItem(versionedKey, JSON.stringify(state));
    } catch (error) {
      console.error(`Error writing to localStorage key ${versionedKey}:`, error);
    }
  }, [versionedKey, state]);

  return [state, setState];
};

export default usePersistentState;
